import { validateRaceCoverFile, RaceCoverValidationError, type RaceCoverSlot } from "../utils/raceCoverAssets";
import { deleteObject, getBlob, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, storage } from "../firebase";
import {
  getInsuranceCertificateError,
  MAX_INSURANCE_CERTIFICATE_BYTES,
  RaceInsuranceCertificate,
} from "../types/raceInsurance";

type UploadFileInput = {
  file: File;
  storagePath: string;
  contentType?: string;
};

const sanitizePathSegment = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return "unknown";
  }

  return normalized.replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-");
};

const getFileExtension = (file: File, fallback = "bin"): string => {
  const byName = file.name.split(".").pop()?.trim().toLowerCase();
  if (byName) {
    return byName;
  }

  const byType = file.type.split("/").pop()?.trim().toLowerCase();
  if (byType) {
    if (byType === "jpeg") {
      return "jpg";
    }
    if (byType === "mpeg") {
      return "mp3";
    }
    return byType;
  }

  return fallback;
};

const createAssetFileName = (
  file: File,
  fallbackExtension = "bin",
  requiredExtension?: string
): string => {
  const extension = requiredExtension || getFileExtension(file, fallbackExtension);
  const baseName = file.name.replace(/\.[^.]+$/, "");
  const safeBaseName = sanitizePathSegment(baseName) || "asset";
  const rawRandomSuffix =
    (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`) || "";
  const randomSuffix = rawRandomSuffix.replace(/[^a-zA-Z0-9-_]/g, "-");
  return `${safeBaseName}-${randomSuffix}.${extension}`;
};

const uploadFileAndGetURL = async (input: UploadFileInput): Promise<string> => {
  const metadata = input.contentType
    ? {
        contentType: input.contentType,
      }
    : undefined;
  const storageRef = ref(storage, input.storagePath);
  await uploadBytes(storageRef, input.file, metadata);
  return getDownloadURL(storageRef);
};

const createRacePhotosBasePath = (raceId: string): string =>
  `racePhotos/races/${requireInsurancePathSegment(raceId)}`;

const createRaceAudioBasePath = (raceId: string): string =>
  `audioFiles/races/${requireInsurancePathSegment(raceId)}`;

const createUserProfileBasePath = (userId: string): string =>
  `users/${requireInsurancePathSegment(userId)}/profile/`;

const ownedAssetPath = (basePath: string): string =>
  `${basePath}/owners/${requireInsurancePathSegment(auth.currentUser?.uid || "")}`;

// Firebase UIDs and Firestore race IDs are case-sensitive. Keep them exact so
// certificate paths match both the race and the uploader's Storage permission.
const requireInsurancePathSegment = (value: string): string => {
  const hasControlCharacter = Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
  if (
    !value || value.trim() !== value || value === "." || value === ".." ||
    /[/\\]/.test(value) || hasControlCharacter
  ) {
    throw new Error("race_create_insurance_upload_failed");
  }
  return value;
};

const createInsuranceBasePath = (raceId: string, userId: string): string =>
  `insuranceCertificates/races/${requireInsurancePathSegment(raceId)}/owners/${requireInsurancePathSegment(userId)}/`;

export const raceAssetUploadService = {
  async uploadInsuranceCertificate(input: {
    raceId: string;
    userId: string;
    certificateFile: File;
  }): Promise<RaceInsuranceCertificate> {
    const validationError = getInsuranceCertificateError(input.certificateFile);
    if (validationError) {
      throw new Error(validationError);
    }

    const contentType = input.certificateFile.type;
    const extension = contentType === "application/pdf" ? "pdf" : contentType === "image/png" ? "png" : "jpg";
    const storagePath = `${createInsuranceBasePath(input.raceId, input.userId)}${createAssetFileName(input.certificateFile, extension, extension)}`;
    await uploadBytes(ref(storage, storagePath), input.certificateFile, {
      contentType,
      contentDisposition: "attachment",
      customMetadata: {
        raceId: input.raceId,
        uploadedBy: input.userId,
      },
    });

    // A download token would make this sensitive document accessible without
    // authentication. Store only its private path in the insurance record.
    return {
      storagePath,
      fileName: input.certificateFile.name,
      contentType,
      sizeBytes: input.certificateFile.size,
    };
  },

  async getInsuranceCertificateBlob(input: {
    raceId: string;
    userId: string;
    certificate: RaceInsuranceCertificate;
  }): Promise<Blob> {
    const basePath = createInsuranceBasePath(input.raceId, input.userId);
    const storagePath = input.certificate.storagePath;
    const fileName = storagePath.slice(basePath.length);
    if (!storagePath.startsWith(basePath) || !fileName || /[/\\]/.test(fileName)) {
      throw new Error("race_create_insurance_upload_failed");
    }

    // getBlob uses the signed-in Firebase session and enforces Storage rules;
    // getDownloadURL would instead expose a persistent bearer-token URL.
    return getBlob(ref(storage, storagePath), MAX_INSURANCE_CERTIFICATE_BYTES);
  },

  async uploadRouteGpx(input: { raceId: string; gpxFile: File; versioned?: boolean }): Promise<string> {
    const userId = auth.currentUser?.uid;
    if (!userId || !input.raceId || /[/\\]/.test(input.raceId)) {
      throw new Error("Could not create race.");
    }
    // An editor stages a new object so a later document-save failure cannot
    // replace the GPX that the currently published race still references.
    // Preserve case: Firestore IDs and Firebase UIDs are case-sensitive.
    const storagePath = `gpxFiles/races/${input.raceId}/owners/${userId}/${createAssetFileName(input.gpxFile, "gpx", "gpx")}`;

    try {
      return await uploadFileAndGetURL({
        file: input.gpxFile,
        storagePath,
        contentType: input.gpxFile.type || "application/gpx+xml",
      });
    } catch {
      // Race creation translates this existing message in every supported
      // locale; the course editor maps it to its localized upload-retry copy.
      throw new Error("Could not create race.");
    }
  },

  async uploadRaceCoverImage(input: { raceId: string; slot: RaceCoverSlot; imageFile: File }): Promise<string> {
    await validateRaceCoverFile(input.imageFile, input.slot);
    const userId = auth.currentUser?.uid;
    if (!userId || !/^[A-Za-z0-9_-]+$/.test(input.raceId) || /[/\\]/.test(userId)) {
      throw new RaceCoverValidationError(input.slot, "The cover image could not be uploaded. Try again.");
    }
    const extension = input.imageFile.type === "image/png" ? "png" : "jpg";
    const fileName = createAssetFileName(input.imageFile, extension, extension);
    const storageRef = ref(storage, `raceCovers/races/${input.raceId}/owners/${userId}/${input.slot}/${fileName}`);
    try {
      await uploadBytes(storageRef, input.imageFile, {
        contentType: input.imageFile.type,
        customMetadata: { raceId: input.raceId, uploadedBy: userId, assetSlot: input.slot },
      });
      return await getDownloadURL(storageRef);
    } catch {
      throw new RaceCoverValidationError(input.slot, "The cover image could not be uploaded. Try again.");
    }
  },

  async uploadRaceGalleryPhoto(input: { raceId: string; imageFile: File }): Promise<string> {
    const fileName = createAssetFileName(input.imageFile, "jpg");

    return uploadFileAndGetURL({
      file: input.imageFile,
      storagePath: `${ownedAssetPath(createRacePhotosBasePath(input.raceId))}/photos/${fileName}`,
      contentType: input.imageFile.type || "image/jpeg",
    });
  },

  async uploadCheckpointImage(input: {
    raceId: string;
    checkpointId: string;
    imageFile: File;
  }): Promise<string> {
    const fileName = createAssetFileName(input.imageFile, "jpg");
    const checkpointId = sanitizePathSegment(input.checkpointId);

    return uploadFileAndGetURL({
      file: input.imageFile,
      storagePath: `${ownedAssetPath(createRacePhotosBasePath(input.raceId))}/checkpoints/${checkpointId}/${fileName}`,
      contentType: input.imageFile.type || "image/jpeg",
    });
  },

  async uploadRaceCueImage(input: {
    raceId: string;
    raceCueId: string;
    imageFile: File;
  }): Promise<string> {
    const fileName = createAssetFileName(input.imageFile, "jpg");
    const raceCueId = sanitizePathSegment(input.raceCueId);

    return uploadFileAndGetURL({
      file: input.imageFile,
      storagePath: `${ownedAssetPath(createRacePhotosBasePath(input.raceId))}/raceCues/${raceCueId}/${fileName}`,
      contentType: input.imageFile.type || "image/jpeg",
    });
  },

  async uploadRaceCueAudio(input: {
    raceId: string;
    raceCueId: string;
    audioFile: File;
  }): Promise<string> {
    const fileName = createAssetFileName(input.audioFile, "mp3");
    const raceCueId = sanitizePathSegment(input.raceCueId);

    return uploadFileAndGetURL({
      file: input.audioFile,
      storagePath: `${ownedAssetPath(createRaceAudioBasePath(input.raceId))}/raceCues/${raceCueId}/${fileName}`,
      contentType: input.audioFile.type || "audio/mpeg",
    });
  },

  async uploadAidStationImage(input: {
    raceId: string;
    aidStationId: string;
    imageFile: File;
  }): Promise<string> {
    const fileName = createAssetFileName(input.imageFile, "jpg");
    const aidStationId = sanitizePathSegment(input.aidStationId);

    return uploadFileAndGetURL({
      file: input.imageFile,
      storagePath: `${ownedAssetPath(createRacePhotosBasePath(input.raceId))}/aidStations/${aidStationId}/${fileName}`,
      contentType: input.imageFile.type || "image/jpeg",
    });
  },

  async uploadSponsorImage(input: {
    raceId: string;
    sponsorId: string;
    imageFile: File;
  }): Promise<string> {
    const fileName = createAssetFileName(input.imageFile, "jpg");
    const sponsorId = sanitizePathSegment(input.sponsorId);

    return uploadFileAndGetURL({
      file: input.imageFile,
      storagePath: `${ownedAssetPath(createRacePhotosBasePath(input.raceId))}/sponsors/${sponsorId}/${fileName}`,
      contentType: input.imageFile.type || "image/jpeg",
    });
  },

  async uploadUserProfilePhoto(input: { userId: string; imageFile: File }): Promise<string> {
    const fileName = createAssetFileName(input.imageFile, "jpg");
    const userId = requireInsurancePathSegment(input.userId);

    return uploadFileAndGetURL({
      file: input.imageFile,
      storagePath: `users/${userId}/profile/${fileName}`,
      contentType: input.imageFile.type || "image/jpeg",
    });
  },

  async deleteUserProfilePhotoByUrl(input: {
    userId: string;
    photoUrl: string | null | undefined;
  }): Promise<void> {
    const photoUrl = input.photoUrl?.trim();
    if (!photoUrl) {
      return;
    }

    try {
      const storageRef = ref(storage, photoUrl);
      const expectedPrefix = createUserProfileBasePath(input.userId);

      if (!storageRef.fullPath.startsWith(expectedPrefix)) {
        return;
      }

      await deleteObject(storageRef);
    } catch (error) {
      // Ignore deletes for external URLs or already-removed files.
      console.warn("Unable to delete previous profile photo.", error);
    }
  },

  async deleteRacePhotoByUrl(input: {
    raceId: string;
    photoUrl: string | null | undefined;
  }): Promise<void> {
    const photoUrl = input.photoUrl?.trim();
    if (!photoUrl) {
      return;
    }

    try {
      const storageRef = ref(storage, photoUrl);
      const expectedPrefix = `${createRacePhotosBasePath(input.raceId)}/`;

      if (!storageRef.fullPath.startsWith(expectedPrefix)) {
        return;
      }

      await deleteObject(storageRef);
    } catch (error) {
      // Ignore deletes for external URLs or already-removed files.
      console.warn("Unable to delete race photo.", error);
    }
  },
};
